import * as React from "react"
import { AlertCircle, CheckCircle2, Download, FileUp, Upload } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { validateAlfredBulkImportFile } from "@/lib/alfred-bulk-import"
import { cn } from "@/lib/utils"
import type { AlfredBulkImportSelection } from "@/types/alfred-bulk-import"

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface AlfredBulkUploadStepProps {
  requiredClientId?: string
  validClientIds: ReadonlySet<string>
  selection: AlfredBulkImportSelection | null
  onSelectionChange: (selection: AlfredBulkImportSelection | null) => void
  onContinue: () => void
}

export function AlfredBulkUploadStep({
  requiredClientId,
  validClientIds,
  selection,
  onSelectionChange,
  onContinue,
}: AlfredBulkUploadStepProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isValidating, setIsValidating] = React.useState(false)
  const [isDragging, setIsDragging] = React.useState(false)

  const processFile = async (file: File) => {
    onSelectionChange(null)
    setIsValidating(true)
    const result = await validateAlfredBulkImportFile(file, {
      requiredClientId,
      validClientIds,
    })
    onSelectionChange({ file, validation: result })
    setIsValidating(false)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) void processFile(file)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files[0]
    if (file && !isValidating) void processFile(file)
  }

  const clearFile = () => {
    onSelectionChange(null)
    if (inputRef.current) inputRef.current.value = ""
  }

  const selectedFile = selection?.file
  const validation = selection?.validation

  return (
    <Card className="w-full max-w-3xl" data-tour="bulk-add">
      <CardHeader>
        <CardTitle>Upload CSV</CardTitle>
        <CardDescription>
          {requiredClientId
            ? `Choose a CSV containing devices for ${requiredClientId}.`
            : "Choose a CSV file containing the Alfred devices you want to add."}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div
          role="group"
          aria-label="CSV upload drop zone"
          className={cn(
            "flex min-h-56 flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-border p-8 text-center transition-colors",
            isDragging && "border-primary bg-primary/5",
          )}
          onDragEnter={(event) => {
            event.preventDefault()
            setIsDragging(true)
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            event.preventDefault()
            setIsDragging(false)
          }}
          onDrop={handleDrop}
        >
          <FileUp className="size-10 text-muted-foreground" aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <p className="font-medium text-foreground">Drag and drop your CSV file</p>
            <p className="text-sm text-muted-foreground">CSV only, up to 10 MB and 10,000 data rows</p>
          </div>
          <input
            ref={inputRef}
            aria-label="CSV file"
            className="sr-only"
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={isValidating}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={isValidating}
          >
            <Upload data-icon="inline-start" />
            {isValidating ? "Validating…" : "Select CSV"}
          </Button>
        </div>

        {selectedFile && validation ? (
          <Alert variant={validation.isValid ? "default" : "destructive"}>
            {validation.isValid ? <CheckCircle2 /> : <AlertCircle />}
            <AlertTitle>{selectedFile.name}</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <p>{formatFileSize(selectedFile.size)}</p>
              {validation.isValid ? (
                <div className="flex flex-col items-start gap-3">
                  <p>
                    {validation.rows.length === 1
                      ? "1 row ready to preview"
                      : `${validation.rows.length} rows ready to preview`}
                  </p>
                  <Button type="button" size="sm" onClick={onContinue}>
                    {validation.rows.length === 1
                      ? "Review 1 device"
                      : `Review ${validation.rows.length} devices`}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p>
                    {validation.issues.length === 1
                      ? "1 validation issue found"
                      : `${validation.issues.length} validation issues found`}
                  </p>
                  <ScrollArea
                    role="region"
                    aria-label="CSV validation issues"
                    className="h-48 pr-4"
                  >
                    <ul className="flex list-disc flex-col gap-1 pl-5">
                      {validation.issues.map((issue) => (
                        <li key={`${issue.rowNumber ?? "file"}-${issue.field ?? "file"}-${issue.message}`}>
                          {issue.message}
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </div>
              )}
              <Button type="button" variant="ghost" size="sm" className="self-start" onClick={clearFile}>
                Clear file
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-foreground">Need the correct columns?</p>
          <p className="text-xs text-muted-foreground">Start with the Alfred bulk import template.</p>
        </div>
        <Button asChild variant="outline">
          <a
            href="/templates/alfred_bulk_import_template.csv"
            download="alfred_bulk_import_template.csv"
          >
            <Download data-icon="inline-start" />
            Download template
          </a>
        </Button>
      </CardFooter>
    </Card>
  )
}
